import { Button } from '@/components/Button';
import { ChooseTemplateSwiper } from '@/components/ChooseTemplateSwiper';
import { Slide3Template } from '@/templates/Slide3Template';
import { useUserData } from '@/contexts/UserDataContext';
import { type AnalogyData } from '@/types/userData';
import { useState } from 'react';

interface NumberOfDaysAnalogyStepProps {
    onNext: () => void;
    totalLostDays: number;
}

const daysToAnalogiesMap = {
    12: [{ text1: "💸", text2: "It's like working a full-time job for a month straight and not seeing a single cent for it." },
    { text1: "🧙", text2: "That's enough time to read the entire Harry Potter series three times over, cover to cover." },
    { text1: "💔", text2: "It's like skipping a whole month of weekend hangouts with your best friends." },
    { text1: "🗣️", text2: "You could've learned enough of a new language to get by comfortably in another country." },
    { text1: "🗓️", text2: "It's as if your two-week vacation got wiped off the calendar completely." }
    ],
    24: [{ text1: "✍️", text2: "It's like writing a 100K word novel from start to finish and not getting a single page published." },
    { text1: "✂️", text2: "It's as if every weekend for a whole year got cut in half, leaving no time to chill." },
    { text1: "✈️", text2: "That's enough time to fly from New York to Amsterdam and back 22 times over." },
    { text1: "⏳", text2: "It's like losing a full month of your waking life every single year." },
    { text1: "🚴", text2: "It's like biking the whole Tour de France route twice, start to finish." }
    ],
    49: [{ text1: "🧍", text2: "It's like missing every weekend with your family and friends for almost a full year." },
    { text1: "🚶", text2: "That's enough time to walk from New York to Los Angeles, coast to coast." },
    { text1: "🎒", text2: "It's like losing a month-long backpacking trip across Southeast Asia, one and a half times over." },
    { text1: "😴", text2: "That's enough time to take a two-hour nap every day for a whole year." },
    { text1: "🗣", text2: "It's like the time you would spend to become fluent in a complex new language." }
    ],
    73: [{ text1: "🌎", text2: "That's like driving around the world more than twice, start to finish." },
    { text1: "❌", text2: "It's like losing every Friday, Saturday, and Sunday for half a year." },
    { text1: "⏰", text2: "It's as if you slept through your alarm and missed the whole morning every day for a year. " },
    { text1: "🎓", text2: "That's enough time to complete a full university semester's worth of classes." },
    { text1: "⛄", text2: "It's like losing an entire winter every year, just gone from your life." }
    ],
    97: [{ text1: "🎒", text2: "It's like missing a month-long backpacking trip across Australia, three times over." },
    { text1: "⛵", text2: "That's enough time to sail across the Atlantic and back, twice over." },
    { text1: "📅", text2: "It's like living on a five-day week instead of seven for a whole year." },
    { text1: "🏃", text2: "That's enough time to run a marathon every week for over half a year. " },
    { text1: "🏞", text2: "It's like exploring every major U.S. National Park, start to finish." }
    ],
}

export const NumberOfDaysAnalogyStep = ({ onNext, totalLostDays }: NumberOfDaysAnalogyStepProps) => {
    const { updateUserData } = useUserData();
    const [selectedSlide, setSelectedSlide] = useState<number>(0);

    const handleSlideChange = (currentSlide: number) => {
        setSelectedSlide(currentSlide);
    };

    const handleNext = () => {
        const slides = daysToAnalogiesMap[totalLostDays as keyof typeof daysToAnalogiesMap];
        const selectedAnalogy: AnalogyData = {
            text1: slides[selectedSlide].text1,
            text2: slides[selectedSlide].text2,
        };

        updateUserData({ selectedAnalogy });
        onNext();
    };

    const slides = daysToAnalogiesMap[totalLostDays as keyof typeof daysToAnalogiesMap];
    const slideTemplates = slides.map((slide) => <Slide3Template text1={slide.text1} text2={slide.text2} />);

    return (
        <>
            <div className='flex-1 flex flex-col justify-center items-center gap-4 w-full overflow-y-auto'>
                <ChooseTemplateSwiper templates={slideTemplates} onSlideChange={handleSlideChange} />
            </div>
            <div className="shrink-0 flex justify-center items-center w-full px-8">
                <Button label="This one" onClick={handleNext} />
            </div>
        </>
    );
};